<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import type { PlatformUserRowFieldsFragment } from '@shared/api/graphql/gql/graphql';

import { getPlatformUserDisplayName, getWorkingPlatformMemberships } from '@/entities/platform';
import { AppButton, AppSelect, BaseModal } from '@/shared/ui';

import { useImpersonation } from '../model/use-impersonation';

interface Props {
  show: boolean;
  user: PlatformUserRowFieldsFragment | null;
  organizationId?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  organizationId: null,
});

const emit = defineEmits<{
  close: [];
}>();

const selectedOrganizationId = ref<string | null>(null);
const { isStarting, start } = useImpersonation();

const userName = computed(() => (props.user ? getPlatformUserDisplayName(props.user) : 'пользователя'));
const organizationOptions = computed(() =>
  props.user
    ? getWorkingPlatformMemberships(props.user).map(({ organization }) => ({
        value: organization.id,
        label: organization.title,
      }))
    : [],
);

watch(
  () => [props.show, props.user?.user.id, props.organizationId] as const,
  ([show]) => {
    if (!show) {
      return;
    }

    const requestedOrganization = organizationOptions.value.find(({ value }) => value === props.organizationId);
    selectedOrganizationId.value = requestedOrganization?.value ?? organizationOptions.value[0]?.value ?? null;
  },
  { immediate: true },
);

const updateOrganization = (value: number | string | (number | string)[] | null): void => {
  selectedOrganizationId.value = typeof value === 'string' && value ? value : null;
};

const submit = async (): Promise<void> => {
  if (!props.user || !selectedOrganizationId.value) {
    return;
  }

  const started = await start({
    targetUserId: props.user.user.id,
    organizationId: selectedOrganizationId.value,
  });

  if (started) {
    emit('close');
  }
};
</script>

<template>
  <BaseModal
    :show="show"
    :persistent="isStarting"
    size="md"
    @close="emit('close')"
  >
    <template #title>Войти как пользователь</template>

    <div class="space-y-5">
      <div class="rounded-xl border border-(--impersonation-accent)/30 bg-(--impersonation-accent)/8 p-4">
        <p class="text-sm font-semibold">{{ userName }}</p>
        <p class="mt-1 text-xs leading-5 text-(--text-secondary)">
          Все разрешённые действия будут выполнены в контексте этой учётной записи и записаны в журнал.
        </p>
      </div>

      <label class="block space-y-2">
        <span class="text-sm font-medium">Организация</span>
        <AppSelect
          :model-value="selectedOrganizationId"
          :options="organizationOptions"
          :disabled="isStarting"
          :clearable="false"
          placeholder="Выберите организацию"
          @update:model-value="updateOrganization"
        />
      </label>

      <p
        v-if="organizationOptions.length === 0"
        role="alert"
        class="text-sm text-(--danger)"
      >
        У пользователя нет активного трудоустройства.
      </p>
    </div>

    <template #actions>
      <div class="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <AppButton
          outline
          :disabled="isStarting"
          @click="emit('close')"
        >
          Отмена
        </AppButton>
        <AppButton
          :loading="isStarting"
          :disabled="!selectedOrganizationId || isStarting"
          @click="submit"
        >
          Войти в режим
        </AppButton>
      </div>
    </template>
  </BaseModal>
</template>
